// PromptLibraryPage.jsx  — UPDATED VERSION
// Save in: src/quora/PromptLibraryPage.jsx  (replaces existing file)
//
// Changes from previous version:
//   1. {brand_voice} chip added to WEBSITE_VARIABLES and YOUTUBE_VARIABLES

import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Input, Select, Table, Tag, Space, Typography,
  Tooltip, Popconfirm, Empty, Modal, Divider
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  YoutubeOutlined, GlobalOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../components/Axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import GeneratePromptModal from './GeneratePromptModal';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

// ── Variable chips ────────────────────────────────────────────────────────────
// {brand_voice} added to both lists

const WEBSITE_VARIABLES = [
  { label: '{question}',         color: 'blue',    description: 'The Quora question text' },
  { label: '{keyword}',          color: 'green',   description: 'Your selected keyword' },
  { label: '{cta}',              color: 'orange',  description: 'Your selected call-to-action' },
  { label: '{basic_info}',       color: 'purple',  description: 'Basic info from your angle' },
  { label: '{existing_answers}', color: 'cyan',    description: 'Existing Quora answers' },
  { label: '{brand_voice}',      color: 'magenta', description: 'Brand voice & writing style from your angle — silently removed if empty' },
];

const YOUTUBE_VARIABLES = [
  { label: '{question}',         color: 'blue',    description: 'The Quora question text' },
  { label: '{keyword}',          color: 'green',   description: 'Your selected keyword (optional in YouTube mode)' },
  { label: '{cta}',              color: 'orange',  description: 'Your selected call-to-action (optional in YouTube mode)' },
  { label: '{basic_info}',       color: 'purple',  description: 'Basic info from your angle' },
  { label: '{existing_answers}', color: 'cyan',    description: 'Existing Quora answers' },
  { label: '{transcript}',       color: 'red',     description: 'YouTube video transcript — auto-fetched at generation time' },
  { label: '{youtube_url}',      color: 'volcano', description: 'Your YouTube video URL — embed naturally in answer text so Quora auto-plays it' },
  { label: '{brand_voice}',      color: 'magenta', description: 'Brand voice & writing style from your angle — silently removed if empty' },
];

const VariableChips = ({ textareaRef, value, onChange, mode }) => {
  const variables = mode === 'youtube' ? YOUTUBE_VARIABLES : WEBSITE_VARIABLES;

  const insertVariable = (variable) => {
    const el = textareaRef?.current?.resizableTextArea?.textArea || textareaRef?.current;
    if (!el) { onChange((value || '') + variable); return; }
    const start = el.selectionStart ?? (value || '').length;
    const end   = el.selectionEnd   ?? (value || '').length;
    const newVal = (value || '').substring(0, start) + variable + (value || '').substring(end);
    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + variable.length, start + variable.length); }, 0);
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '5px' }}>
        💡 Click a chip to insert variable at cursor{mode === 'youtube' ? ' — YouTube mode includes {transcript} and {youtube_url}' : ''}:
      </Text>
      <Space wrap size={[5, 5]}>
        {variables.map(v => (
          <Tooltip key={v.label} title={v.description} placement="top">
            <Tag
              color={v.color}
              style={{ cursor: 'pointer', fontSize: '11px', padding: '1px 7px', userSelect: 'none', marginRight: 0 }}
              onClick={() => insertVariable(v.label)}
            >
              {v.label}
            </Tag>
          </Tooltip>
        ))}
      </Space>
    </div>
  );
};

const ModeBadge = ({ mode }) => {
  if (mode === 'youtube') return <Tag color="red"  icon={<YoutubeOutlined />} style={{ fontSize: '11px' }}>YouTube</Tag>;
  return                         <Tag color="blue" icon={<GlobalOutlined />}  style={{ fontSize: '11px' }}>Website</Tag>;
};

// ─────────────────────────────────────────────────────────────────────────────
const PromptLibraryPage = () => {
  const storedUser = localStorage.getItem('user');
  const userId = storedUser ? JSON.parse(storedUser)._id : null;

  const [prompts, setPrompts]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);

  const [generateModalVisible, setGenerateModalVisible] = useState(false);

  // form fields
  const [formName, setFormName]       = useState('');
  const [formDesc, setFormDesc]       = useState('');
  const [formContent, setFormContent] = useState('');
  const [formMode, setFormMode]       = useState('website');
  const contentRef = useRef(null);

  // ── Load prompts ──────────────────────────────────────────────────────────
  const loadPrompts = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/prompt-library/${userId}`);
      if (res.data.success) setPrompts(res.data.data);
    } catch {
      toast.error('Failed to load prompt library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPrompts(); }, [userId]);

  // ── Open create form ──────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingPrompt(null);
    setFormName(''); setFormDesc(''); setFormContent(''); setFormMode('website');
    setFormVisible(true);
  };

  // ── Open edit form ────────────────────────────────────────────────────────
  const openEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormName(prompt.name);
    setFormDesc(prompt.description || '');
    setFormContent(prompt.content);
    setFormMode(prompt.mode || 'website');
    setFormVisible(true);
  };

  // ── Save (Add / Edit) ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formName.trim())    { toast.warning('Please enter a prompt name');  return; }
    if (!formContent.trim()) { toast.warning('Please enter prompt content'); return; }

    setSaving(true);
    try {
      if (editingPrompt) {
        const res = await axios.put(`/api/prompt-library/${editingPrompt._id}`, {
          name: formName, description: formDesc, content: formContent, mode: formMode,
        });
        if (res.data.success) {
          toast.success('Prompt updated!');
          setPrompts(prev => prev.map(p => p._id === editingPrompt._id ? res.data.data : p));
          setFormVisible(false);
        }
      } else {
        const res = await axios.post('/api/prompt-library', {
          userId, name: formName, description: formDesc, content: formContent, mode: formMode,
        });
        if (res.data.success) {
          toast.success('Prompt saved to library!');
          setPrompts(prev => [res.data.data, ...prev]);
          setFormVisible(false);
        }
      }
    } catch {
      toast.error('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (promptId) => {
    try {
      const res = await axios.delete(`/api/prompt-library/${promptId}`);
      if (res.data.success) {
        toast.success('Prompt deleted!');
        setPrompts(prev => prev.filter(p => p._id !== promptId));
      }
    } catch {
      toast.error('Failed to delete prompt');
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '13px' }}>{text}</Text>
          {record.description && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      key: 'mode',
      width: 120,
      render: (mode) => <ModeBadge mode={mode} />,
    },
    {
      title: 'Preview',
      dataIndex: 'content',
      key: 'content',
      render: (text) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {text.length > 100 ? text.substring(0, 100) + '…' : text}
        </Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {new Date(date).toLocaleDateString()}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this prompt?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar />

        {/* Main Content */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

          {/* Page Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BookOpen size={22} style={{ color: '#7c3aed' }} />
              </div>
              <div>
                <Title level={4} style={{ margin: 0 }}>Prompt Library</Title>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Create and manage reusable prompts for AI answer generation.
                </Text>
              </div>
            </div>

            {/* ── BUTTONS ROW ── */}
            <Space size={10}>
              {/* Generate with AI button */}
              <Button
                icon={<ThunderboltOutlined />}
                onClick={() => setGenerateModalVisible(true)}
                size="large"
                style={{
                  backgroundColor: '#fff',
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  fontWeight: 600,
                }}
              >
                ✨ Generate New Prompt
              </Button>

              {/* Manual add button */}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreate}
                size="large"
                style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
              >
                Add New Prompt
              </Button>
            </Space>
          </div>

          {/* Info banner */}
          <div style={{
            padding: '12px 16px',
            background: '#f5f3ff',
            border: '1px solid #ddd6fe',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#5b21b6',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <BookOpen size={16} />
            <span>
              Prompts saved here are available in <strong>AI Answer Generation</strong> — click <strong>"📚 Select Prompt"</strong> when generating answers to pick from this library.
              Each prompt is either <strong>YouTube</strong> or <strong>Website</strong> mode — add separately if you want to use the same prompt for both.
            </span>
          </div>

          {/* Prompts Table */}
          <Card bodyStyle={{ padding: '0' }}>
            <Table
              dataSource={prompts}
              columns={columns}
              rowKey="_id"
              loading={loading}
              size="middle"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: '8px', color: '#666' }}>No prompts in your library yet.</div>
                        <Space>
                          <Button
                            icon={<ThunderboltOutlined />}
                            onClick={() => setGenerateModalVisible(true)}
                            style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
                          >
                            ✨ Generate with AI
                          </Button>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                            style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
                          >
                            Add Manually
                          </Button>
                        </Space>
                      </div>
                    }
                  />
                ),
              }}
            />
          </Card>

          {/* Stats row */}
          {prompts.length > 0 && (
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Total: <strong>{prompts.length}</strong> prompt{prompts.length !== 1 ? 's' : ''}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>·</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                YouTube: <strong>{prompts.filter(p => p.mode === 'youtube').length}</strong>
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>·</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Website: <strong>{prompts.filter(p => p.mode === 'website').length}</strong>
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Prompt Modal ────────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {editingPrompt ? <EditOutlined /> : <PlusOutlined />}
            <span>{editingPrompt ? 'Edit Prompt' : 'New Prompt'}</span>
          </div>
        }
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        width={750}
        footer={[
          <Button key="cancel" onClick={() => setFormVisible(false)}>Cancel</Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
            style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
          >
            {editingPrompt ? 'Update Prompt' : 'Save to Library'}
          </Button>,
        ]}
      >
        {/* Name */}
        <div style={{ marginBottom: '14px' }}>
          <Text strong style={{ display: 'block', marginBottom: '6px' }}>
            Prompt Name <Text type="danger">*</Text>
          </Text>
          <Input
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. SaaS Answer with CTA, YouTube Blog Style..."
            showCount
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '14px' }}>
          <Text strong style={{ display: 'block', marginBottom: '6px' }}>
            Description{' '}
            <Text type="secondary" style={{ fontWeight: 'normal', fontSize: '12px' }}>(optional)</Text>
          </Text>
          <Input
            value={formDesc}
            onChange={(e) => setFormDesc(e.target.value)}
            placeholder="Short note about what this prompt does..."
            showCount
          />
        </div>

        {/* Mode */}
        <div style={{ marginBottom: '14px' }}>
          <Text strong style={{ display: 'block', marginBottom: '6px' }}>Works for Mode</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Select value={formMode} onChange={setFormMode} style={{ width: '220px' }}>
              <Option value="youtube">🔴 YouTube Only</Option>
              <Option value="website">🔵 Website Only</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This filters which prompts appear based on mode in the generator.
            </Text>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Content */}
        <div>
          <Text strong style={{ display: 'block', marginBottom: '6px' }}>
            Prompt Content <Text type="danger">*</Text>
          </Text>
          <VariableChips textareaRef={contentRef} value={formContent} onChange={setFormContent} mode={formMode} />
          <TextArea
            ref={contentRef}
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            rows={15}
            style={{ fontFamily: 'monospace', fontSize: '12px', marginTop: '6px' }}
            placeholder={formMode === 'youtube'
              ? `Example (YouTube mode):\n\nAnswer this question as the creator of this video:\n{question}\n\nVideo link to embed naturally: {youtube_url}\nTranscript: {transcript}\nExisting answers: {existing_answers}\n\nIf brand voice is provided, match this exact tone and style: {brand_voice}`
              : `Example (Website mode):\n\nAnswer this Quora question:\n{question}\n\nKeyword: {keyword}\nCTA: {cta}\nBackground: {basic_info}\nExisting answers: {existing_answers}\n\nIf brand voice is provided, match this exact tone and style: {brand_voice}`}
          />
          <Text type="secondary" style={{ fontSize: '11px', marginTop: '6px', display: 'block' }}>
            Variables in curly braces <code style={{ background: '#f0f0f0', padding: '0 3px', borderRadius: '3px' }}>{'{like_this}'}</code> are automatically replaced when generating answers. <code style={{ background: '#f0f0f0', padding: '0 3px', borderRadius: '3px' }}>{'{brand_voice}'}</code> is silently removed if the angle has no brand voice set.
          </Text>
        </div>
      </Modal>

      {/* ── Generate Prompt Modal ──────────────────────────────────────────── */}
      <GeneratePromptModal
        visible={generateModalVisible}
        onClose={() => setGenerateModalVisible(false)}
        onSaved={(newPrompt) => setPrompts(prev => [newPrompt, ...prev])}
        userId={userId}
      />
    </div>
  );
};

export default PromptLibraryPage;